import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sailboat, Menu, X, Globe } from "lucide-react";

export default function Navigation() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const NAV_ITEMS = [
    { key: "home", href: "#home" },
    { key: "about", href: "#about" },
    { key: "fleet", href: "#fleet" },
    { key: "tours", href: "#tours" },
    { key: "booking", href: "#booking" },
  ];

  const current = i18n.resolvedLanguage || i18n.language || "en";
  const other = current.startsWith("mk") ? "en" : "mk";
  const toggle = () => i18n.changeLanguage(other);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "nav-glass" : "nav-glass-dark"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <a
          href="#home"
          data-testid="nav-logo"
          className={`flex items-center gap-3 ${
            scrolled ? "text-[#0A1B35]" : "text-white"
          }`}
        >
          <Sailboat className="w-5 h-5" strokeWidth={1.5} />
          <div className="leading-tight">
            <div className="font-serif-display text-xl">Ars Vivendi</div>
            <div className="overline text-[0.58rem] opacity-80 -mt-0.5">
              {t("nav.tagline")}
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV_ITEMS.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.key}`}
              className={`nav-link text-[0.78rem] tracking-[0.18em] uppercase font-medium ${
                scrolled ? "text-[#0A1B35]" : "text-white/90"
              }`}
            >
              {t(`nav.${n.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Switch language"
            className={`flex items-center gap-1.5 overline text-[0.7rem] tracking-[0.2em] transition-colors ${
              scrolled ? "text-[#0A1B35] hover:opacity-70" : "text-white/90 hover:text-white"
            }`}
          >
            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t(`lang.${other}`)}
          </button>
          <a
            href="#booking"
            data-testid="nav-cta-book"
            className={scrolled ? "btn-primary" : "btn-outline"}
          >
            {t("nav.cta")}
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle-mobile"
            aria-label="Switch language"
            className={`flex items-center gap-1 overline text-[0.7rem] ${
              scrolled ? "text-[#0A1B35]" : "text-white"
            }`}
          >
            <Globe className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t(`lang.${other}`)}
          </button>
          <button
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            className={scrolled ? "text-[#0A1B35]" : "text-white"}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="lg:hidden nav-glass border-t border-black/5"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_ITEMS.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                data-testid={`mnav-link-${n.key}`}
                className="text-[#0A1B35] text-sm tracking-[0.18em] uppercase"
              >
                {t(`nav.${n.key}`)}
              </a>
            ))}
            <a
              href="#booking"
              onClick={() => setOpen(false)}
              data-testid="mnav-cta-book"
              className="btn-primary self-start"
            >
              {t("nav.cta")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
